import { zodResolver } from '@hookform/resolvers/zod'
import { ChangeEvent, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { Avatar } from '../../../components/Avatar'
import { Button } from '../../../components/Button'
import { Input } from '../../../components/Input'
import { completeUserProfile, CompleteUserProfileRequest } from '../../../services/authServices'
import { compressImage } from '../../../utils/compressImage'
import { formatPhoneNumber } from '../../../utils/formatPhoneNumber'

const completeUserRegisterFormSchema = z.object({
  name: z.string(),
  password: z.string().min(6, 'A senha precisa ter pelo menos 6 caracteres'),
  phone: z.string().transform((value) => value.replace(/\D/g, '')),
  passwordConfirmation: z.string(),
}).refine(values => values.password === values.passwordConfirmation, {
  message: 'As senhas estão diferentes',
  path: ['passwordConfirmation'],
})

type completeRegisterFormData = z.infer<typeof completeUserRegisterFormSchema>

interface CompleteRegisterFormProps {
  userId: string
}

export function CompleteRegisterForm({ userId }: CompleteRegisterFormProps) {
  const [avatarFile, setAvatarFile] = useState<File | undefined>()
  const [avatarPreview, setAvatarPreview] = useState<string | undefined>()

  const { register, handleSubmit, setValue, setError, watch, formState: { isSubmitting, errors } } = useForm<completeRegisterFormData>({
    resolver: zodResolver(completeUserRegisterFormSchema),
  })

  const navigate = useNavigate()

  async function handleSubmitCompleteRegister(data: completeRegisterFormData) {
    if (!userId) return
    const userProfile: CompleteUserProfileRequest = {
      id: userId,
      avatar: avatarFile,
      name: data.name,
      phone: data.phone,
      password: data.password,
    }

    const result = await completeUserProfile(userProfile)

    if (result) {
      if (result.success) {
        toast.success(result.message, { position: 'top-left' })
        navigate(`/?userEmail=${result.user?.email}`)
      } else {
        toast.error(result.message, { position: 'top-left' })
        if (result.message.includes('telefone')) {
          setError('phone', {
            type: 'manual',
            message: result.message,
          })
        }
      }
    }
  }

  async function handleChangeAvatar(event: ChangeEvent<HTMLInputElement>) {
    const files = event.target.files
    if (files && files.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const filesList = Array.from(files).map(async (file: any) => {
        if (!file.type.includes('image/')) {
          return null
        }
        const fileExtension = file.name.split('.').pop()
        const renamedFile = new File([file], `${userId}.${fileExtension}`, {
          type: file.type,
        })
        const compressedFile = await compressImage(renamedFile, 1000)
        return compressedFile
      })

      const compressedFiles = await Promise.all(filesList)
      const [file] = compressedFiles.filter(file => file !== null)

      const preview = URL.createObjectURL(file)

      setAvatarFile(file)
      setAvatarPreview(preview)
    }
  }

  const phoneValue = watch('phone')

  return (
    <form
      className="flex-1 space-y-6"
      onSubmit={handleSubmit(handleSubmitCompleteRegister)}
    >
      <div className="flex items-center justify-center">
        <Avatar imageUrl={avatarPreview} className="w-20 h-20" svgClass="w-22 h-22" />
      </div>
      <div>
        <label htmlFor="avatar" className="block text-sm/6 font-medium text-gray-900 mb-2">
          Foto do perfil
        </label>
        <input
          className="block w-full text-sm text-gray-900 border border-gray-300 bg-white file:p-2 file:bg-indigo-600 file:cursor-pointer file:text-white file: rounded-md cursor-pointer file:border-0"
          id="avatar"
          type="file"
          onChange={handleChangeAvatar}
          accept="image/*"
        />
      </div>
      <div>
        <Input
          label="Nome"
          id="name"
          required
          placeholder="Fulano de Tal"
          error={errors.name}
          {...register('name')}
        />
      </div>
      <div>
        <Input
          label="Telefone (WhatsApp)"
          id="phone"
          required
          placeholder="(00) 0 0000-0000"
          error={errors.phone}
          {...register('phone')}
          value={formatPhoneNumber(phoneValue || '')}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setValue('phone', formatPhoneNumber(e.target.value))}
        />
      </div>
      <div>
        <Input
          label="Senha"
          id="password"
          required
          type="password"
          placeholder="******"
          error={errors.password}
          {...register('password')}
        />
      </div>
      <div>
        <Input
          label="Confirmação de senha"
          id="passwordConfirmation"
          type="password"
          required
          placeholder="******"
          error={errors.passwordConfirmation}
          {...register('passwordConfirmation')}
        />
      </div>
      <div className="space-y-2">
        <Button
          type="submit"
          isLoading={isSubmitting}
        >
          Salvar
        </Button>
      </div>
    </form>
  )
}
